import { ReactNode, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { RenderItemParams } from '@atlaskit/tree'
import { Button, FormInput } from '@microservices/wiskey-react-components'
import { Grid, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'
import { Tab, TabPanel, Tabs } from '@components/Tabs'
import { Variables } from '@components/Variables'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import {
  CONTEXT_MENU_APPLICABLE_TO_OPTIONS,
  CONTEXT_MENU_TABS,
  ENTITY_CONFIG_TYPE,
  ENTITY_TYPES_REQUEST,
  GENERATOR_INPUT_TYPE,
} from '@constants'
import { TreeItemMenuPointOptionType } from '@types'

import { ConfigField } from '@gantt/components/GanttCreateOrEdit/types'

import { AddMenuPointDialog } from './components/AddMenuPointDialog'
import { Item } from './components/MenuTab/Item'
import { MenuList } from './components/MenuTab/MenuList'
import { VariablesTab } from './components/VariablesTab'
import { useContextMenuCreateOrEdit } from './hooks/useContextMenuCreateOrEdit'

export type VariableType = ConfigField & { id?: number }

export const ContextMenuCreateOrEdit = () => {
  const { t } = useTranslation()

  const { data, state, handlers } = useContextMenuCreateOrEdit()

  const { currentContextMenu, isEdit, isLoadingContextMenu } = data
  const {
    methods,
    currentTab,
    editTreeItemForm,
    fields,
    showAddDialog,
    tree,
    objectsOptions,
    systemsOptions,
    loadingObjects,
    loadingSystems,
    watchedObject,
    globalDirty,
    watchVariables,
    watchApplicableTo,
  } = state

  const {
    setTree,
    handleDeleteTreeItem,
    handleEditTreeItem,
    handleClickAddTreeItem,
    handleClickAddMenuPoint,
    handleDragEnd,
    handleSaveMenuPoint,
    handleSaveContextMenu,
    handleChangeTab,
    handleCloseMenuPointDialog,
    handleAddItem,
    handleRemoveItem,
    handleSaveVariable,
    handleDeleteVariable,
    handleCancelContextMenu,
    handleSetConfigCommandInternalId,
  } = handlers

  const treeNames = useMemo(
    () => Object.values(tree?.items ?? {}).map(op => op.data.title.toLowerCase()),
    [tree]
  )

  const renderItem = ({ item, ...props }: RenderItemParams): ReactNode => {
    return (
      <Item
        {...props}
        item={item as TreeItemMenuPointOptionType}
        onAddTreeItem={handleClickAddTreeItem}
        onDeleteTreeItem={handleDeleteTreeItem}
        onEditTreeItem={handleEditTreeItem}
      />
    )
  }

  return (
    <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.CONTEXT_MENUS}>
      <PageContentLayout sx={{ p: 1 }}>
        <EditConfigScriptDialog />
        <Grid container alignItems={'center'} justifyContent={'space-between'} py={0.5}>
          <Typography mb={1} variant='h5'>
            {t(`contextMenuCreate.title`)}
          </Typography>
          {currentContextMenu?.id && <EditConfigMenu configId={currentContextMenu?.id} />}
        </Grid>
        <FormProvider {...methods}>
          <FormInput
            inputType={GENERATOR_INPUT_TYPE.INPUT}
            label={t('contextMenuCreate.form.title.label')}
            labelSx={{ width: 170, minWidth: 170 }}
            name={'title'}
            placeholder={t('contextMenuCreate.form.title.placeholder')}
          />
          <FormInput
            autocompleteOptions={objectsOptions}
            disabled={Boolean(currentContextMenu)}
            inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
            label={t('contextMenuCreate.form.object.label')}
            labelSx={{ width: 170, minWidth: 170 }}
            loading={loadingObjects}
            name={'object'}
            placeholder={t('contextMenuCreate.form.object.placeholder')}
          />
          <FormInput
            autocompleteOptions={[...CONTEXT_MENU_APPLICABLE_TO_OPTIONS]}
            inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
            label={t('contextMenuCreate.form.applicableTo.label')}
            labelSx={{ width: 170, minWidth: 170 }}
            name={'applicableTo'}
            placeholder={t('contextMenuCreate.form.applicableTo.placeholder')}
          />

          <Grid container alignItems={'center'} justifyContent={'space-between'}>
            <Tabs
              allowScrollButtonsMobile
              scrollButtons
              value={currentTab}
              variant='scrollable'
              onChange={handleChangeTab}
            >
              <Tab
                label={t('contextMenuCreate.tabs.menuPoints')}
                value={CONTEXT_MENU_TABS.MENU_POINT}
              />
              <Tab
                label={t('contextMenuCreate.tabs.dynamicVariables')}
                value={CONTEXT_MENU_TABS.DYNAMIC_VARIABLES}
              />
              <Tab
                label={t('contextMenuCreate.tabs.staticVariables')}
                value={CONTEXT_MENU_TABS.STATIC_VARIABLES}
              />
            </Tabs>
            {currentTab === CONTEXT_MENU_TABS.MENU_POINT && (
              <Button variant='contained' onClick={handleClickAddMenuPoint}>
                {t('contextMenuCreate.addMenuPoint')}
              </Button>
            )}
            {currentTab === CONTEXT_MENU_TABS.DYNAMIC_VARIABLES && (
              <Button disabled={!watchedObject} variant='contained' onClick={handleAddItem}>
                {t('contextMenuCreate.addVariable')}
              </Button>
            )}
          </Grid>

          <TabPanel index={CONTEXT_MENU_TABS.MENU_POINT} value={currentTab}>
            <MenuList
              renderItem={renderItem}
              setTree={setTree}
              tree={tree}
              onDragEnd={handleDragEnd}
            />
          </TabPanel>
          <TabPanel index={CONTEXT_MENU_TABS.DYNAMIC_VARIABLES} value={currentTab}>
            <VariablesTab
              variables={fields}
              onDeleteVariable={handleDeleteVariable}
              onRemove={handleRemoveItem}
              onSaveVariable={handleSaveVariable}
            />
          </TabPanel>

          <TabPanel index={CONTEXT_MENU_TABS.STATIC_VARIABLES} value={currentTab}>
            <Variables
              entityId={currentContextMenu?.id}
              entityType={ENTITY_TYPES_REQUEST.CONTEXT_MENU}
              isEditEntity={isEdit}
            />
          </TabPanel>

          <Grid container gap='15px' justifyContent={'flex-end'} width={'100%'}>
            <GeneralButtons
              cancelText={t('contextMenuCreate.form.cancelButton')}
              isLoading={isLoadingContextMenu}
              saveDisabled={!globalDirty || isLoadingContextMenu}
              saveText={t('contextMenuCreate.form.saveButton')}
              onCancel={handleCancelContextMenu}
              onSave={handleSaveContextMenu}
            />
          </Grid>
        </FormProvider>

        {(showAddDialog || editTreeItemForm) && (
          <AddMenuPointDialog
            applicableTo={watchApplicableTo}
            isLoadingObjects={loadingObjects}
            isLoadingSystems={loadingSystems}
            item={editTreeItemForm}
            objectCode={watchedObject?.id}
            objectTitle={watchedObject?.label}
            objectsOptions={objectsOptions}
            systemsOptions={systemsOptions}
            treeNames={treeNames}
            variables={watchVariables}
            onClose={handleCloseMenuPointDialog}
            onSave={handleSaveMenuPoint}
            onSetConfigCommandInternalId={handleSetConfigCommandInternalId}
          />
        )}
      </PageContentLayout>
    </EditConfigProvider>
  )
}
